import React, { useContext, useEffect } from 'react';
import { IconArrowRight } from '@tabler/icons-react';
import { View } from '@/styles/reactnative';
import {
  TransferFromToWrapper,
  SelectWrapper,
  StyledSelect,
  SelectLabel,
} from '../styles';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface TransferFromToProps {
  transferFrom: string;
  setTransferFrom: (value: string) => void;
  transferTo: string;
  setTransferTo: (value: string) => void;
}

const TransferFromTo: React.FC<TransferFromToProps> = ({
  transferFrom,
  setTransferFrom,
  transferTo,
  setTransferTo,
}) => {
  const { gray, textw, bgColor } = useContext(ThemeContext).theme.COLORS;
  const { isDarkMode } = useContext(ThemeContext);
  const { t } = useTranslation();

  // 보낸지갑 옵션
  const fromOptions = ['spotwallet', 'futurewallet', 'rewardwallet', 'botwallet', 'copywallet'];

  // 받는지갑 옵션 (보낸지갑에 따라 필터링)
  const getToOptions = () => {
    if (transferFrom === 'spotwallet') {
      return ['futurewallet', 'botwallet', 'copywallet'];
    } else if (transferFrom === 'rewardwallet') {
      return ['futurewallet'];
    } else if (transferFrom === 'futurewallet') {
      return ['spotwallet', 'botwallet', 'copywallet'];
    } else if (transferFrom === 'botwallet') {
      return ['spotwallet', 'futurewallet'];
    } else if (transferFrom === 'copywallet') {
      return ['spotwallet', 'futurewallet'];
    }
    return ['spotwallet', 'futurewallet'];
  };

  // 보낸지갑 변경 시 받는지갑 자동 조정
  useEffect(() => {
    const availableToOptions = getToOptions();
    if (!availableToOptions.includes(transferTo)) {
      setTransferTo(availableToOptions[0]);
    }
  }, [transferFrom]);

  // 보낸지갑 선택 시 실행되는 함수
  const handleFromChange = (e) => {
    const selectedWallet = e.target.value;
    setTransferFrom(selectedWallet);

    // 리워드지갑을 선택한 경우에는 받는지갑을 선물지갑으로 설정
    if (selectedWallet === 'rewardwallet') {
      setTransferTo('futurewallet');
    }
    // 봇/카피 지갑을 선택한 경우에는 받는지갑을 현물지갑으로 설정
    else if (selectedWallet === 'botwallet' || selectedWallet === 'copywallet') {
      setTransferTo('spotwallet');
    }
  };

  // 받는지갑 선택 시 실행되는 함수
  const handleToChange = (e) => {
    setTransferTo(e.target.value);
  };

  return (
    <TransferFromToWrapper>
      <SelectWrapper>
        <SelectLabel>{t('from')}</SelectLabel>
        <StyledSelect value={transferFrom} onChange={handleFromChange}>
          {fromOptions.map((option) => (
            <option key={option} value={option}>
              {t(option)}
            </option>
          ))}
        </StyledSelect>
      </SelectWrapper>

      <SwapButtonContainer>
        <SwapButton
          disabled={
            transferFrom === 'rewardwallet' ||
            transferFrom === 'botwallet' ||
            transferFrom === 'copywallet' ||
            transferTo === 'botwallet' ||
            transferTo === 'copywallet'
          }
          onClick={() => {
            // 리워드지갑 및 봇/카피 지갑 관련 제약조건을 고려하여 스왑
            if (
              transferFrom === 'rewardwallet' ||
              transferFrom === 'botwallet' ||
              transferFrom === 'copywallet'
            ) {
              // 리워드, 봇, 카피 지갑에서는 스왑하지 않음
              return;
            }
            // 스왑 로직
            const tempFrom = transferFrom;
            setTransferFrom(transferTo);
            setTransferTo(tempFrom);
          }}
          isDarkMode={isDarkMode}
        >
          <IconArrowRight
            size={16}
            color={
              transferFrom === 'rewardwallet' ||
              transferFrom === 'botwallet' ||
              transferFrom === 'copywallet' ||
              transferTo === 'botwallet' ||
              transferTo === 'copywallet'
                ? '#7F8C8D'
                : '#007AFF'
            }
          />
        </SwapButton>
      </SwapButtonContainer>

      <SelectWrapper>
        <SelectLabel>{t('to')}</SelectLabel>
        <StyledSelect value={transferTo} onChange={handleToChange}>
          {getToOptions().map((option) => (
            <option key={option} value={option}>
              {t(option)}
            </option>
          ))}
        </StyledSelect>
      </SelectWrapper>
    </TransferFromToWrapper>
  );
};

// Styled Components
const SwapButtonContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 8px;
`;

const SwapButton = styled.button<{ isDarkMode: boolean; disabled: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.disabled ? '#7F8C8D' : '#007AFF')};
  background: ${(props) =>
    props.isDarkMode
      ? props.disabled
        ? 'rgba(127, 140, 141, 0.1)'
        : 'linear-gradient(135deg, rgba(0, 122, 255, 0.2) 0%, rgba(51, 167, 255, 0.1) 100%)'
      : props.disabled
        ? 'rgba(127, 140, 141, 0.1)'
        : 'linear-gradient(135deg, rgba(0, 122, 255, 0.12) 0%, rgba(94, 198, 255, 0.06) 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);

  &:hover {
    ${(props) =>
      !props.disabled &&
      `
      transform: scale(1.1) rotate(180deg);
      box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
      border-color: #33A7FF;
    `}
  }

  &:active {
    ${(props) =>
      !props.disabled &&
      `
      transform: scale(1.05) rotate(180deg);
    `}
  }

  svg {
    transition: all 0.3s ease;
  }
`;

export default TransferFromTo;
